import React from 'react';
import { useTheme, type Team, type Mode } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  compact?: boolean;
}

function TeamGlyph({ which, active }: { which: Team; active: boolean }) {
  const stroke = active ? 'var(--accent-ink)' : 'var(--text-muted)';
  return which === 'hornets' ? (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={stroke} strokeWidth="1.6">
      <path d="M7 1 L13 7 L7 13 L1 7 Z" />
      <path d="M4 7 L7 4 L10 7 L7 10 Z" fill={stroke} opacity=".4" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={stroke} strokeWidth="1.6">
      <path d="M7 1 L12 4 L12 10 L7 13 L2 10 L2 4 Z" />
      <circle cx="7" cy="7" r="2" fill={stroke} opacity=".4" />
    </svg>
  );
}

function btnClass(active: boolean, compact: boolean) {
  const pad = compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[11px]';
  const base =
    'flex items-center justify-center gap-1.5 rounded-xs font-mono font-medium uppercase tracking-[0.2px] transition-colors';
  return `${base} ${pad} ${
    active
      ? 'bg-accent text-accent-ink'
      : 'bg-transparent text-text-muted hover:text-text'
  }`;
}

export default function ThemeToggle({ compact = false }: ThemeToggleProps) {
  const { team, mode, setTeam, setModePreference } = useTheme();

  const setTeamSafe = (t: Team) => setTeam(t);
  const setModeSafe = (m: Mode) => setModePreference(m);

  return (
    <div className="inline-flex items-center gap-[2px] p-[2px] rounded-xs bg-surface border border-border">
      <button
        type="button"
        className={btnClass(team === 'hornets', compact)}
        onClick={() => setTeamSafe('hornets')}
        title="Hornets palette"
        aria-label="Hornets palette"
      >
        <TeamGlyph which="hornets" active={team === 'hornets'} />
        {!compact && 'H'}
      </button>
      <button
        type="button"
        className={btnClass(team === 'panthers', compact)}
        onClick={() => setTeamSafe('panthers')}
        title="Panthers palette"
        aria-label="Panthers palette"
      >
        <TeamGlyph which="panthers" active={team === 'panthers'} />
        {!compact && 'P'}
      </button>
      <div className="w-px h-4 bg-border mx-0.5" />
      <button
        type="button"
        className={btnClass(mode === 'dark', compact)}
        onClick={() => setModeSafe('dark')}
        title="Dark mode"
        aria-label="Dark mode"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M10 7a4 4 0 1 1-5-5 3 3 0 0 0 5 5z" />
        </svg>
        {!compact && 'DARK'}
      </button>
      <button
        type="button"
        className={btnClass(mode === 'light', compact)}
        onClick={() => setModeSafe('light')}
        title="Light mode"
        aria-label="Light mode"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="6" cy="6" r="2.3" />
          <path
            d="M6 1v1.5M6 9.5V11M1 6h1.5M9.5 6H11M2.5 2.5l1 1M8.5 8.5l1 1M2.5 9.5l1-1M8.5 3.5l1-1"
            strokeLinecap="round"
          />
        </svg>
        {!compact && 'LIGHT'}
      </button>
    </div>
  );
}
